// ─── L'incasso: SI FA AL BANCO, IN UN GESTO SOLO ─────────────────────────────
//
// 💶 § 03.5: «prima di incassare mostra chi paga, quanto, con che metodo, e — se
// è borsellino — il saldo attuale e quello che resterà. Un incasso col
// borsellino che scopre lo scoperto *dopo* il click è un incasso che va
// stornato.»
//
// ⭐ E tutto quello che serve a dirlo arriva con la scheda della prenotazione,
// in una richiesta sola: gli incassi già registrati e il saldo di ciascuno. Un
// saldo chiesto quando il modulo si apre è un saldo che arriva dopo che la
// segreteria ha già cominciato a battere l'importo.
//
// 🔄⭐⭐ **DAL 17/09/2026 NON SI INCASSA PIÙ UNA PERSONA PER VOLTA.**
// 🗣️ Sua proposta, disegnata: una spunta per giocatore, l'importo, il mezzo, e
//   **un bottone solo** in fondo — *«rendere facile e semplice a chi è di
//   segreteria effettuare velocemente un incasso»*, e *«non bisogna far aprire
//   nuove finestre»*.
// 📏 **Cosa costava la forma vecchia, contata sulla partita da quattro della
//   sua fotografia**: quattro «Incassa», quattro moduli aperti e richiusi,
//   quattro mezzi scelti, quattro conferme ⇒ **16 gesti**. Adesso: spuntare
//   «tutti», premere ⇒ **2**.
//
// 🔄🚨⭐⭐ **E LA SERA DEL 17/09 È CADUTO ANCHE IL «MODO INCASSO»: LA SCHEDA È
// UNA SOLA, SEMPRE LA STESSA.**
// 🗣️ Sue parole, guardando le due schermate una accanto all'altra: *«non
//   capisco perché mi hai fatto due schede simili. A me ne basta solo una…
//   non possiamo far sì che ce n'è una sola di scheda dove c'è tutto?»*.
// ⚖️ **Aveva ragione, ed è lo stesso difetto del «Togli» sparito di qualche ora
//   prima, alla sua radice**: un bottone «Incassa» che cambiava le colonne
//   della tabella *era* una seconda scheda — stesso riquadro, contenuto
//   diverso, e nessuna delle due mostrava tutto. Chi sta al banco non sa in
//   quale delle due si trova, e per vedere «a carico» doveva uscire
//   dall'incasso.
// 📌 *Due schermate non diventano una perché stanno nella stessa cornice: lo
//    diventano quando non si deve più scegliere quale guardare.*
// ⇒ Le colonne ci sono **tutte insieme** (spunta · chi · a carico · incassa ·
//   mezzo · ✕), e non c'è più niente da aprire o da chiudere. Le spunte
//   partono **vuote**: con il bottone sempre in vista, righe già spuntate
//   vorrebbero dire che un clic solo, dato per sbaglio, incassa da tutti.
//
// 🔒 **E il gruppo parte in UNA richiesta** (`POST /payments/batch`), non in
// quattro: se una riga viene rifiutata non ne resta scritta nessuna. Con quattro
// chiamate in fila, al primo rifiuto due sarebbero passate e due no — e chi sta
// al banco dovrebbe ricostruire a mano a che punto era.
//
// 🩹 Della forma vecchia è caduto un `<dialog>` che si apriva SOPRA la scheda, e
// con lui una cura che va ricordata: il `close` di un dialogo annidato **non
// sale nel DOM**, ma React ascolta in fase di cattura e lo ripropaga lungo il
// PROPRIO albero — quindi chiudere l'incasso chiudeva anche la scheda.
// 📌 *Un evento che nel browser non sale può salire in React: le due
//    propagazioni sono due cose diverse.*

import { useState } from 'react';
import {
  ETICHETTE_METODO,
  METODI_INCASSO,
  chiaveIncasso,
  esaminaIncasso,
  formatCents,
  parseCents,
  type CassaDellaPrenotazione,
  type EsameIncasso,
  type MetodoIncasso,
  type RigaDaIncassare,
  VALORE_CREDITO_CENTS,
} from '@pmo/shared';
import { usaIncassoInBlocco } from './dati.js';
import {
  RIGA_INTATTA,
  giaPagatoCents,
  importoDellaRiga,
  inEuro,
  residuoCents,
  type ChiPaga,
  type Scelta,
  type SceltaGrezza,
} from './riga-incasso.js';

export interface IncassoRapido {
  /** Com'è messa la riga di questa persona adesso. */
  scelta(chi: ChiPaga): Scelta;
  spunta(chi: ChiPaga, v: boolean): void;
  scriviImporto(chi: ChiPaga, testo: string): void;
  /**
   * 🔗 Rimette questa riga a **seguire il residuo**, buttando via la cifra
   * battuta a mano. Lo chiama la scheda quando cambia l'importo **a carico** di
   * questa persona: da quel momento «quanto deve» è un altro numero, e una
   * casella che continuasse a mostrare il precedente incasserebbe il vecchio.
   */
  seguiIlCarico(chi: ChiPaga): void;
  scegliMezzo(chi: ChiPaga, m: MetodoIncasso): void;
  /** Il mezzo di TUTTE le righe spuntate: al banco di solito pagano allo stesso modo. */
  mezzoPerTutti(m: MetodoIncasso): void;
  tutti(v: boolean): void;
  /** Rimette tutto com'era: nessuno spuntato, nessuna nota, nessun guasto. */
  azzera(): void;
  nota: string;
  scriviNota(t: string): void;
  /** Quanto resterà nel borsellino a questa persona, se paga col borsellino. */
  borsellino(chi: ChiPaga): { saldo: number; dopo: number } | null;
  /**
   * 🎁 I crediti premio che questa persona può usare QUI: solo su una partita,
   * solo un socio che ne ha. `0` ⇒ il mezzo «Crediti premio» non compare.
   */
  crediti(chi: ChiPaga): number;
  esame: EsameIncasso;
  invia(): void;
  inCorso: boolean;
  errore: unknown;
  /**
   * ⚠️ Cosa il server NON ha scritto, e perché: una riga che torna con
   * `creato: false` è un incasso **già registrato**, non un incasso fatto
   * adesso. `null` = l'ultimo gesto è andato tutto a segno.
   */
  giaRegistrato: string | null;
}

/**
 * ⭐⭐ **LO STATO DELL'INCASSO STA QUI, E NON DENTRO LA TABELLA**, perché i due
 * pezzi che lo mostrano vivono in due punti diversi della scheda: le righe nel
 * corpo che scorre, il totale e il bottone nel **piede che non scorre**.
 * ⚖️ Tenerlo nella tabella vorrebbe dire rimettere il bottone in fondo
 * all'elenco — cioè dove per premerlo bisogna sapere che c'è.
 */
export function usaIncassoRapido(p: {
  id: string;
  players: ChiPaga[];
  cassa: CassaDellaPrenotazione;
  /** 🎁 Il tipo: i crediti premio pagano solo le partite. */
  type?: string;
}): IncassoRapido {
  const [scelte, setScelte] = useState<Record<string, SceltaGrezza>>({});
  const [nota, setNota] = useState('');
  const [giaRegistrato, setGiaRegistrato] = useState<string | null>(null);
  const incassa = usaIncassoInBlocco(p.id);

  /*
   * ⚠️ La riga di chi non ce l'ha ancora si calcola al volo, e non è un caso di
   * scuola: un giocatore entra in campo mentre si sta compilando l'incasso, e
   * senza questo ripiego la sua riga sarebbe una casella vuota senza mezzo.
   * ⭐ È anche ciò che rimette tutto a posto dopo un incasso riuscito: basta
   *   svuotare le scelte, e ogni riga riparte dal residuo che il server ha
   *   appena ricalcolato — zero, per chi ha pagato.
   */
  const grezza = (chi: ChiPaga): SceltaGrezza => scelte[chi.id] ?? RIGA_INTATTA;
  /**
   * ⭐ Qui `null` diventa un numero, e **ogni volta da capo**: è il punto in cui
   * il legame con «a carico» si rifà — `residuoCents` legge `chi`, che la
   * scheda ricostruisce a ogni rilettura dal server.
   */
  const scelta = (chi: ChiPaga): Scelta => {
    const g = grezza(chi);
    return { ...g, importo: importoDellaRiga(g.importo, p.cassa, chi, p.players) };
  };
  const cambia = (chi: ChiPaga, c: Partial<SceltaGrezza>): void =>
    setScelte((s) => ({ ...s, [chi.id]: { ...grezza(chi), ...c } }));

  const righe: RigaDaIncassare[] = p.players.map((g) => {
    const s = scelta(g);
    return {
      playerId: g.id,
      memberId: g.memberId,
      displayName: g.displayName,
      method: s.metodo,
      amountCents: s.metodo === 'gift' ? 0 : parseCents(s.importo),
      /**
       * 💶 Quanto ha già pagato ADESSO, letto dalla scheda che il server ha
       * appena mandato: è il pezzo che rende la chiave capace di distinguere
       * una seconda rata da una ripetizione della prima (⇒ `chiaveIncasso`).
       */
      giaPagatoCents: giaPagatoCents(p.cassa, g, p.players),
      scelto: s.scelto,
    };
  });

  const esame = esaminaIncasso(righe, p.cassa.saldi, p.id, p.cassa.premi ?? []);

  const crediti = (chi: ChiPaga): number =>
    p.type === 'partita' && chi.memberId !== null
      ? ((p.cassa.premi ?? []).find((x) => x.memberId === chi.memberId)?.credits ?? 0)
      : 0;

  const azzera = (): void => {
    setScelte({});
    setNota('');
    setGiaRegistrato(null);
    incassa.reset();
  };

  return {
    scelta,
    spunta: (chi, v) => cambia(chi, { scelto: v }),
    scriviImporto: (chi, testo) => cambia(chi, { importo: testo }),
    seguiIlCarico: (chi) => cambia(chi, { importo: null }),
    scegliMezzo(chi, m) {
      /*
       * ⭐ Un omaggio vale zero, e la casella lo dice invece di lasciare in
       * vista una cifra che non verrà incassata. Tornando a un mezzo vero si
       * riprende il residuo: altrimenti resterebbe uno zero da cancellare a
       * mano, cioè un rifiuto travestito da casella.
       */
      const prima = grezza(chi);
      /*
       * 🎁 Coi crediti premio si paga a euro interi e fino a quanti ne ha: la
       * casella propone quello che i crediti coprono, e il resto si incassa dopo
       * con un altro mezzo (la riga torna col residuo).
       */
      if (m === 'premio') {
        const residuo = residuoCents(p.cassa, chi, p.players);
        const copre =
          Math.min(Math.floor(residuo / VALORE_CREDITO_CENTS), crediti(chi)) * VALORE_CREDITO_CENTS;
        cambia(chi, { metodo: m, importo: inEuro(copre) });
      } else if (m === 'gift') cambia(chi, { metodo: m, importo: '0,00' });
      else if (prima.metodo === 'gift') cambia(chi, { metodo: m, importo: null });
      else cambia(chi, { metodo: m });
    },
    /**
     * 🔄🚨 **VALE PER TUTTE LE RIGHE, E FINO AL 18/09/2026 VALEVA SOLO PER
     * QUELLE GIÀ SPUNTATE.** ⇒ Chi sceglieva il mezzo **prima** di spuntare —
     * che è l'ordine naturale quando si sa già come pagano — non cambiava
     * niente: il menu tornava a «mezzo…», le righe restavano su «Contanti», e
     * l'incasso partiva col mezzo sbagliato **senza che niente lo dicesse**.
     * ⚖️ Applicarlo a tutte non incassa da nessuno in più: partono solo le righe
     *   spuntate, e il mezzo di una riga che non parte non fa danno. ⇒ Fra un
     *   gesto che a volte non fa niente e uno che prepara anche righe che forse
     *   non serviranno, il secondo non ha vittime.
     * 📌 *Un comando che funziona solo in un ordine, e tace nell'altro, non è un
     *    comando: è un comando e una trappola.*
     */
    mezzoPerTutti(m) {
      // 🎁 I crediti sono di una persona: non si scelgono «per tutti».
      if (m === 'premio') return;
      setScelte((s) => {
        const nuovo = { ...s };
        for (const g of p.players) {
          const attuale = nuovo[g.id] ?? RIGA_INTATTA;
          nuovo[g.id] = {
            ...attuale,
            metodo: m,
            importo: m === 'gift' ? '0,00' : attuale.metodo === 'gift' ? null : attuale.importo,
          };
        }
        return nuovo;
      });
    },
    tutti(v) {
      setScelte((s) => {
        const nuovo = { ...s };
        for (const g of p.players) nuovo[g.id] = { ...(nuovo[g.id] ?? RIGA_INTATTA), scelto: v };
        return nuovo;
      });
    },
    azzera,
    nota,
    scriviNota: setNota,
    borsellino(chi) {
      if (chi.memberId === null) return null;
      const saldo = p.cassa.saldi.find((s) => s.memberId === chi.memberId)?.balanceCents ?? 0;
      const s = scelta(chi);
      return { saldo, dopo: saldo - (parseCents(s.importo) ?? 0) };
    },
    crediti,
    esame,
    invia() {
      if (esame.manca !== null) return;
      setGiaRegistrato(null);
      const daMandare = righe
        .filter((r) => r.scelto)
        .map((r) => ({
          memberId: r.memberId,
          /** ⭐ Il posto in campo: è l'identità di un ospite, che il nome non è. */
          playerId: r.playerId,
          playerName: r.displayName,
          method: r.method,
          amountCents: r.amountCents ?? 0,
          /**
           * 🚨 LA CHIAVE È DETERMINISTICA, e qui si può calcolarla al clic
           * proprio perché lo è: due clic, due schede, due computer arrivano
           * tutti alla stessa chiave — quindi alla stessa riga.
           * ⚖️ È l'opposto del borsellino, dove la chiave nasce con la
           * finestra. Là la domanda è «è lo stesso GESTO?», qui è «è lo stesso
           * FATTO?».
           */
          idempotencyKey: chiaveIncasso({
            bookingId: p.id,
            memberId: r.memberId,
            playerId: r.playerId,
            playerName: r.displayName,
            method: r.method,
            amountCents: r.amountCents ?? 0,
            giaPagatoCents: r.giaPagatoCents,
          }),
        }));
      incassa.mutate(
        { bookingId: p.id, reason: nota.trim() === '' ? null : nota.trim(), righe: daMandare },
        /*
         * ⭐ A cose fatte le spunte si tolgono e la nota si svuota: le righe
         * restano dove sono, con «pagato» aggiornato e il residuo a zero. È
         * quello che prima faceva la chiusura del modulo — solo che adesso non
         * c'è niente da chiudere, e a chiudersi è il GESTO, non la finestra.
         */
        {
          /**
           * 🚨⭐⭐ **UN `creato: false` SI DICE, e fino al 18/09/2026 questa
           * schermata lo buttava via.** Il contratto lo prevede da sempre
           * (`esitoIncassoSchema`: *«la schermata lo dice»*) — solo che nessuno
           * lo leggeva: il gruppo tornava, le spunte si svuotavano, e una riga
           * **non scritta** era indistinguibile da una scritta.
           * ⚖️ È la metà che trasforma un difetto in un danno: quando il primo
           *   difetto faceva sparire un ripagamento, era proprio questo silenzio
           *   a far credere che fosse andato a buon fine.
           * 📌 *Una schermata che mostra lo stesso volto a «fatto» e a «non ho
           *    fatto niente» non informa: rassicura.*
           */
          onSuccess: (esito) => {
            const ripetuti = esito.esiti.filter((e) => !e.creato);
            setGiaRegistrato(
              ripetuti.length === 0
                ? null
                : ripetuti.length === esito.esiti.length
                  ? 'Questo incasso era già registrato: non ne è stato scritto un altro.'
                  : `${ripetuti.length} di questi incassi erano già registrati: di quelli non è stato scritto niente di nuovo.`,
            );
            setScelte({});
            setNota('');
          },
        },
      );
    },
    inCorso: incassa.isPending,
    errore: incassa.error,
    giaRegistrato,
  };
}

/** Il menu dei mezzi, uguale nella riga e nel «per tutti». */
export function MenuMezzo({
  valore,
  cambia,
  etichetta,
  disabilitato,
  crediti = 0,
}: {
  valore: MetodoIncasso | '';
  cambia(m: MetodoIncasso): void;
  etichetta: string;
  disabilitato?: boolean;
  /** 🎁 I crediti premio di chi paga: il mezzo compare solo se ne ha. */
  crediti?: number;
}): React.JSX.Element {
  return (
    <select
      className="mezzo"
      aria-label={etichetta}
      value={valore}
      disabled={disabilitato}
      onChange={(e) => cambia(e.target.value as MetodoIncasso)}
    >
      {valore === '' && <option value="">mezzo…</option>}
      {METODI_INCASSO.filter((m) => m !== 'premio' || crediti > 0 || valore === 'premio').map((m) => (
        <option key={m} value={m}>
          {m === 'premio' ? `🎁 Crediti (${crediti})` : ETICHETTE_METODO[m]}
        </option>
      ))}
    </select>
  );
}

/** Quanto si sta per prendere, detto in euro. */
export function totaleInEuro(esame: EsameIncasso): string {
  return formatCents(esame.totaleCents);
}
